"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { usePathname, useRouter } from "next/navigation"
import {
  ChevronDown,
  LogOut,
  LayoutDashboard,
  Mail,
  Megaphone,
  ImageIcon,
  Users,
  Target,
  BarChart3,
  Settings,
  HelpCircle,
  Plus,
  Zap,
  MessageSquare,
  ThumbsUp,
  FileText,
  FolderOpen,
  Brain,
  Bell,
  Server,
  CalendarClock,
  ShieldCheck,
  Smartphone,
} from "lucide-react"

import { useAuth } from "@/lib/auth-context"
import { useClientContext } from "@/lib/client-context"
import { resolvePortalRole, RolePermissions, getRoleLabel } from "@/lib/rbac"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
// portal-nav kept for APP_NAME/APP_DESCRIPTOR used in layout
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"

const mainNavItems = [
  {
    title: "Dashboard",
    url: "/",
    icon: LayoutDashboard,
  },
  {
    title: "Campaigns",
    url: "/campaigns",
    icon: Megaphone,
    subItems: [
      { title: "All Campaigns", url: "/campaigns" },
      { title: "Create New", url: "/campaigns/create" },
    ],
  },
  {
    title: "Media Library",
    url: "/media",
    icon: FolderOpen,
    subItems: [
      { title: "All Media", url: "/media" },
      { title: "Scheduled Posts", url: "/scheduled" },
    ],
  },
  {
    title: "Audiences",
    url: "/audiences",
    icon: Target,
  },
  {
    title: "Leads",
    url: "/leads",
    icon: Users,
  },
  {
    title: "Analytics",
    url: "/analytics",
    icon: BarChart3,
  },
  {
    title: "Clients",
    url: "/clients",
    icon: Users,
  },
  {
    title: "Messages",
    url: "/messages",
    icon: MessageSquare,
  },
  {
    title: "Email Composer",
    url: "/email-composer",
    icon: Mail,
  },
  {
    title: "SMS Composer",
    url: "/sms-composer",
    icon: Smartphone,
  },
  {
    title: "Feedback",
    url: "/feedback",
    icon: ThumbsUp,
  },
  {
    title: "Notifications",
    url: "/notifications",
    icon: Bell,
  },
  {
    title: "AI Insights",
    url: "/ai-insights",
    icon: Brain,
  },
  {
    title: "Cold Outreach",
    url: "/cold-email",
    icon: Server,
    subItems: [
      { title: "Dashboard", url: "/cold-email" },
      { title: "New Cluster", url: "/cold-email/clusters?create=true" },
      { title: "Approvals", url: "/cold-email/approvals" },
      { title: "Provider Status", url: "/cold-email/provider-status" },
    ],
  },
  {
    title: "RFQ",
    url: "/rfq",
    icon: FileText,
  },
  {
    title: "A2P / 10DLC",
    url: "/settings/a2p",
    icon: ShieldCheck,
  },
]

const bottomNavItems = [
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
    subItems: [
      { title: "Profile & Account", url: "/settings" },
      { title: "Team Management", url: "/team" },
      { title: "A2P / 10DLC", url: "/settings/a2p" },
    ],
  },
  {
    title: "Help & Support",
    url: "/help",
    icon: HelpCircle,
  },
]

function ActiveClientBadge() {
  const { activeClient, setActiveClient, clearClient, canSwitchClients, assignedClients } = useClientContext()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  if (!mounted) return null

  if (!activeClient) {
    return (
      <div className="rounded-lg border border-dashed border-muted-foreground/30 p-3 text-center">
        <p className="text-xs text-muted-foreground">No client selected</p>
        <Link href="/clients" className="text-xs text-primary hover:underline">
          Select a client
        </Link>
      </div>
    )
  }

  // Show client switcher if user can switch between multiple clients
  if (canSwitchClients && assignedClients.length > 1) {
    return (
      <div className="rounded-lg bg-primary/10 border border-primary/20 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-primary">Switch Client</span>
          <button
            onClick={clearClient}
            className="text-[10px] text-muted-foreground hover:text-destructive transition-colors"
          >
            ✕
          </button>
        </div>
        <Select value={activeClient._id} onValueChange={(clientId) => {
          const selected = assignedClients.find(c => c._id === clientId)
          if (selected) setActiveClient(selected)
        }}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {assignedClients.map((client) => (
              <SelectItem key={client._id} value={client._id}>
                <span className="text-xs">
                  {((client as any).firstname && (client as any).lastname)
                    ? `${(client as any).firstname} ${(client as any).lastname}`
                    : (client.firstName && client.lastName)
                    ? `${client.firstName} ${client.lastName}`
                    : client.email}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  const clientDisplayName =
    (activeClient as any).firstname && (activeClient as any).lastname
      ? `${(activeClient as any).firstname} ${(activeClient as any).lastname}`
      : (activeClient as any).firstname
      ? (activeClient as any).firstname
      : (activeClient as any).lastname
      ? (activeClient as any).lastname
      : activeClient.firstName && activeClient.lastName
      ? `${activeClient.firstName} ${activeClient.lastName}`
      : activeClient.firstName
      ? activeClient.firstName
      : activeClient.lastName
      ? activeClient.lastName
      : activeClient.email
      ? activeClient.email
      : (activeClient as any).companyName
      ? (activeClient as any).companyName
      : ""

  return (
      <div className="rounded-lg bg-primary/10 border border-primary/20 p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-primary">Active Client</span>
          {canSwitchClients ? (
            <button
              onClick={clearClient}
            className="text-[10px] text-muted-foreground hover:text-destructive transition-colors"
          >
            ✕
          </button>
          ) : (
            <span className="text-[10px] text-muted-foreground">Assigned</span>
          )}
        </div>
      <p className="text-sm font-medium text-sidebar-foreground truncate">
        {clientDisplayName}
      </p>
      {activeClient.email && (
        <p className="text-xs text-muted-foreground truncate">{activeClient.email}</p>
      )}
    </div>
  )
}

export function AppSidebar() {
  const pathname = usePathname()
  const { user, logout } = useAuth()
  const { canSwitchClients } = useClientContext()
  const router = useRouter()
  const businessName =
    typeof user?.businessName === "string" && user.businessName.trim()
      ? user.businessName
      : "My Business"

  const handleLogout = () => {
    logout()
    router.push("/login")
  }

  const normalizedRole = resolvePortalRole(user)
  const roleLabel = getRoleLabel(normalizedRole)

  const visibleMainNavItems = mainNavItems.filter((item) => {
    if (normalizedRole === "admin") return true
    if (item.url === "/clients") return canSwitchClients
    if (item.url === "/cold-email" || item.url === "/settings/a2p") return RolePermissions.canAccessColdEmail(normalizedRole)
    return true
  })

  const visibleBottomNavItems = bottomNavItems.map((item) => {
    if (item.title !== "Settings") return item
    return {
      ...item,
      subItems: (item.subItems || []).filter((sub) => {
        if (sub.url === "/team") return RolePermissions.canAccessTeamSettings(normalizedRole)
        if (sub.url === "/settings/a2p") return RolePermissions.canAccessA2P(normalizedRole)
        return true
      }),
    }
  })

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
        <div className="flex items-center gap-3">
          <img src="/images/24hr-logo.png" alt="24HR Marketing Hub" className="h-9 w-9 shrink-0 rounded-lg object-contain" />
          <div className="flex flex-col group-data-[collapsible=icon]:hidden">
            <span className="text-sm font-semibold text-sidebar-foreground">24HR Marketing Hub</span>
            <span className="text-xs text-muted-foreground">Marketing Portal</span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Marketing Portal
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleMainNavItems.map((item) => {
                const isActive = pathname === item.url || pathname.startsWith(item.url + "/")

                if (item.subItems) {
                  return (
                    <Collapsible key={item.title} defaultOpen={isActive}>
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            type="button"
                            isActive={isActive}
                            className="pr-2 [&[data-state=open]_.chevron-icon]:rotate-180"
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            <ChevronDown className="chevron-icon ml-auto h-4 w-4 opacity-80 transition-transform duration-200" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub className="border-l-0">
                            {item.subItems.map((sub) => (
                              <SidebarMenuSubItem key={sub.url}>
                                <SidebarMenuSubButton asChild isActive={pathname === sub.url}>
                                  <Link href={sub.url}>{sub.title}</Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )
                }

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <Link href={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4 space-y-3">
        <div className="group-data-[collapsible=icon]:hidden">
          <ActiveClientBadge />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-3 rounded-lg p-2 hover:bg-sidebar-accent transition-colors">
              <Avatar className="h-8 w-8">
                <AvatarImage src="/placeholder-user.jpg" alt="User" />
                <AvatarFallback className="bg-primary/20 text-primary text-xs">
                  {`${(user?.firstName as string)?.[0] || ""}${(user?.lastName as string)?.[0] || ""}`.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start text-left flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                <span className="text-sm font-medium text-sidebar-foreground truncate w-full">
                  {user?.firstname ? `${user.firstname} ${user.lastname || ""}` : "User"}
                </span>
                <span className="text-xs text-muted-foreground truncate w-full">
                  {(user?.email as string) || "—"}
                </span>
                <Badge variant="secondary" className="mt-1 text-[10px] uppercase tracking-wide">
                  {roleLabel}
                </Badge>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground group-data-[collapsible=icon]:hidden" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem asChild><Link href="/settings">Profile Settings</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link href="/team">Team Management</Link></DropdownMenuItem>
            <DropdownMenuItem>Billing</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={handleLogout}>Sign Out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
